import {
  ComponentProps,
  RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {createPortal} from 'react-dom';
import {twMerge} from 'tailwind-merge';

import CloseIcon from '@/assets/icons/cross.svg?react';

export type BaseModalProps = ComponentProps<'div'> & {
  fullScreen?: boolean;
  backdropProps?: Omit<ComponentProps<'div'>, 'onClick'>;
  backdropRef?: RefObject<HTMLDivElement>;
  appearSmoothBackdropAnimation?: boolean;
  disappearSmoothBackdropAnimation?: boolean;
  onClose?: () => void;
  withCloseButton?: boolean;
};

export default function BaseModal({
  children,
  className,
  backdropProps = {},
  fullScreen = false,
  appearSmoothBackdropAnimation = false,
  disappearSmoothBackdropAnimation = false,
  onClose,
  withCloseButton = false,
}: BaseModalProps) {
  const {className: backdropClassName, ...restBackdropProps} = backdropProps;
  const [appeared, setAppeared] = useState(false);
  const [canCloseByBackdrop, setCanCloseByBackdrop] = useState(
    !appearSmoothBackdropAnimation
  );

  useEffect(() => {
    setAppeared(true);
  }, []);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setCanCloseByBackdrop(true);
    }, 75);

    return () => {
      clearTimeout(timerId);
    };
  }, []);

  const handleByBackdropClick = useCallback(() => {
    if (!canCloseByBackdrop) {
      return;
    }

    onClose?.();
  }, [canCloseByBackdrop, onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        {...restBackdropProps}
        role={onClose ? 'dialog' : undefined}
        className={twMerge(
          'absolute h-full w-full bg-black bg-opacity-50',
          appearSmoothBackdropAnimation &&
            'opacity-0 transition-opacity duration-75',
          appearSmoothBackdropAnimation &&
            appeared &&
            !disappearSmoothBackdropAnimation &&
            'opacity-100',
          disappearSmoothBackdropAnimation &&
            'opacity-0 transition-opacity duration-75',
          backdropClassName
        )}
        {...(onClose ? {onClick: handleByBackdropClick} : {})}
      ></div>
      <div
        className={twMerge(
          'relative z-10 my-auto max-h-[86dvh] w-fit max-w-[94dvw] sm:max-h-[90dvh]',
          fullScreen && 'my-0 h-full max-h-none max-w-none sm:max-h-none',
          className
        )}
      >
        {withCloseButton && (
          <button
            className="absolute top-5 right-6 z-[51] text-gray-500 hover:text-gray-700 focus:outline-none"
            onClick={onClose}
          >
            <CloseIcon className="h-6 w-6" />
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
}
